import {
  ArrowDownIcon,
  ArrowUpIcon,
  DotsThreeIcon,
  PushPinIcon,
  PushPinSlashIcon,
  XIcon,
} from '@phosphor-icons/react'
import React, { useMemo } from 'react'

import { Checkbox } from '../checkbox'
import { DropdownMenu, DropdownMenuItem } from '../dropdown-menu'
import { Icon } from '../icon'
import { BorderLoader } from '../loaders/border-loader'
import { Tooltip } from '../tooltip'
import * as S from './table.styled'
import {
  getColumnId,
  SortOrder,
  TableColumn,
  TableColumnMenuContext,
  TableHeadProps,
} from './table.types'
import { getHeaderActionsWidth } from './table.utils'

interface TableHeaderCellProps<T = unknown> {
  column: TableColumn<T>
  menuContext: TableColumnMenuContext<T>
  enablePinning?: boolean
  isPinned: boolean
  isSortedAsc: boolean
  isSortedDesc: boolean
  onPinClick: () => void
  onSortAsc?: () => void
  onSortDesc?: () => void
  onClearSort?: () => void
  onToggleSort?: () => void
}

const TableHeaderCell = <T,>({
  column,
  menuContext,
  enablePinning = true,
  isPinned,
  isSortedAsc,
  isSortedDesc,
  onPinClick,
  onSortAsc,
  onSortDesc,
  onClearSort,
  onToggleSort,
}: TableHeaderCellProps<T>) => {
  const isSortable = !!column.sortKey
  const isSorted = isSortedAsc || isSortedDesc

  // Note: Columns without a real (string) title - e.g. an in-array "ghost" action column that
  // only renders a button - aren't eligible for pinning; there's nothing meaningful to label the
  // pin menu item with.
  const canPin = enablePinning && !!column.title

  const menuItems = useMemo<DropdownMenuItem[]>(
    () => [
      {
        hidden: !isSortable,
        icon: ArrowUpIcon,
        label: column.sortAscLabel ?? 'Sort ascending',
        onClick: onSortAsc,
      },
      {
        hidden: !isSortable,
        icon: ArrowDownIcon,
        label: column.sortDescLabel ?? 'Sort descending',
        onClick: onSortDesc,
      },
      {
        disabled: !isSorted,
        hidden: !isSorted,
        icon: XIcon,
        label: 'Clear sorting',
        onClick: onClearSort,
      },
      {
        hidden: !canPin,
        icon: isPinned ? PushPinSlashIcon : PushPinIcon,
        label: isPinned ? 'Unpin column' : 'Pin column',
        onClick: onPinClick,
      },
      ...(column.getMenuItems?.(menuContext) ?? []),
    ],
    [
      column,
      menuContext,
      column.sortAscLabel,
      column.sortDescLabel,
      canPin,
      isPinned,
      isSortable,
      isSorted,
      onClearSort,
      onPinClick,
      onSortAsc,
      onSortDesc,
    ],
  )

  const showDropdownMenu = menuItems.length > 0 && menuItems.some((item) => !item.hidden)

  return (
    <>
      <S.HeaderCellContent
        data-header-content
        $actionsWidth={getHeaderActionsWidth(column, enablePinning)}
      >
        <S.HeaderCellTitle>{column.title}</S.HeaderCellTitle>
        {column.tooltip && <Tooltip>{column.tooltip}</Tooltip>}
      </S.HeaderCellContent>
      <S.ColumnActionsWrapper data-column-actions>
        {isPinned && (
          <Tooltip
            trigger={
              // Note: the same 24x24 header button as sort / "...", always shown on a pinned column.
              <S.HeaderActionButton
                onlyBox
                data-visible
                use="ghost"
                aria-label="Unpin column"
                onClick={onPinClick}
              >
                <Icon icon={PushPinSlashIcon} size="1.4rem" weight="fill" />
              </S.HeaderActionButton>
            }
          >
            Unpin column
          </Tooltip>
        )}
        {isSortable && (
          <Tooltip
            trigger={
              // Note: a sorted column keeps its arrow shown (data-visible) but at rest - the design
              // has no Active background for "currently sorted", only for hover/press.
              <S.HeaderActionButton
                onlyBox
                use="ghost"
                data-visible={isSorted || undefined}
                onClick={onToggleSort}
              >
                <Icon icon={isSortedDesc ? ArrowDownIcon : ArrowUpIcon} size="1.4rem" />
              </S.HeaderActionButton>
            }
          >
            {isSortedAsc
              ? 'Switch to descending'
              : isSortedDesc
                ? 'Switch to ascending'
                : 'Sort ascending'}
          </Tooltip>
        )}
        {showDropdownMenu && (
          <DropdownMenu
            items={menuItems}
            size="small"
            trigger={({ isOpen }) => (
              <Tooltip
                disabled={isOpen}
                trigger={
                  <S.HeaderActionButton onlyBox use="ghost" active={isOpen}>
                    <Icon icon={DotsThreeIcon} size="1.4rem" weight="bold" />
                  </S.HeaderActionButton>
                }
              >
                More column actions
              </Tooltip>
            )}
          />
        )}
      </S.ColumnActionsWrapper>
    </>
  )
}

export const TableHead = <T,>({
  columns,
  columnWidths,
  data,
  pinnedColumnId,
  enablePinning = true,
  sortBy,
  sortOrder,
  onPinColumn,
  onSort,
  mode = 'regular',
  isAllSelected,
  isIndeterminate,
  isSelectAllLoading,
  onSelectAll,
}: TableHeadProps<T>) => {
  const isBulkMode = mode === 'bulk'

  const handlePinColumn = (columnId: string) => {
    if (!enablePinning) return

    if (pinnedColumnId === columnId) {
      onPinColumn(null)
    } else {
      onPinColumn(columnId)
    }
  }

  const handleSort = (sortKey: string, order: SortOrder) => {
    onSort?.(sortKey, order)
  }

  const handleClearSort = () => {
    onSort?.(null, null)
  }

  const handleSelectAllChange = () => {
    onSelectAll?.(!isAllSelected)
  }

  const menuContext = useMemo<TableColumnMenuContext<T>>(() => ({ columns, data }), [columns, data])

  return (
    <thead>
      <tr>
        {isBulkMode && (
          <S.CheckboxHeaderCell>
            <S.CheckboxWrapper>
              {isSelectAllLoading ? (
                <BorderLoader size="extra-small" />
              ) : (
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isIndeterminate}
                  // Note: per the table design, the header's select-all is the 20px (medium) checkbox.
                  size="medium"
                  onChange={handleSelectAllChange}
                />
              )}
            </S.CheckboxWrapper>
          </S.CheckboxHeaderCell>
        )}
        {columns.map((column) => {
          const columnId = getColumnId(column)
          const isPinned = enablePinning && !!column.title && pinnedColumnId === columnId
          const width = columnWidths[columnId]
          const isSortedAsc = sortBy === column.sortKey && sortOrder === 'asc'
          const isSortedDesc = sortBy === column.sortKey && sortOrder === 'desc'

          const cellProps = {
            column,
            enablePinning,
            isPinned,
            isSortedAsc,
            isSortedDesc,
            menuContext,
            onClearSort: handleClearSort,
            onPinClick: () => handlePinColumn(columnId),
            onSortAsc: column.sortKey ? () => handleSort(column.sortKey!, 'asc') : undefined,
            onSortDesc: column.sortKey ? () => handleSort(column.sortKey!, 'desc') : undefined,
            onToggleSort: column.sortKey
              ? () => handleSort(column.sortKey!, isSortedAsc ? 'desc' : 'asc')
              : undefined,
          }

          if (isPinned) {
            return (
              <S.PinnedHeaderCell
                key={columnId}
                data-column-id={columnId}
                $width={width}
                $isBulkMode={isBulkMode}
                data-highlighted={column.highlighted || undefined}
              >
                <TableHeaderCell {...cellProps} />
              </S.PinnedHeaderCell>
            )
          }

          return (
            <th
              key={columnId}
              data-column-id={columnId}
              data-highlighted={column.highlighted || undefined}
            >
              <TableHeaderCell {...cellProps} />
            </th>
          )
        })}
      </tr>
    </thead>
  )
}
